import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, Suspense, useCallback, useRef, useState, } from 'react';
import { Field, Form } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import { A, ButtonV2, composeValidators, H1, InputCheckbox, InputNumber, InputText, Label, Span, Spinner, validateEmail, validateRequiredField, } from 'shared';
import { useGetServicingV1InstrumentsInstrumentIdAgreementsTermsTemplateNameQuery, useGetServicingV1InstrumentsInstrumentIdQuery, usePostServicingV1InstrumentsInstrumentIdAgreementsTermsAchAuthorizationMutation, usePutServicingV1InstrumentsInstrumentIdAgreementSignMutation, } from 'servicing-api';
import { AchAuthSsnModal, DisclosureFooter, FlowActions, MaritalDisclosureFields, } from '../components';
import SummaryCard from '../components/SummaryCard';
import { useAuthorizationToken, useEffectiveBankAccountId, useFlexFinalizeData, useFlowPath, } from '../hooks';
const AgreementPdfViewer = lazy(() => import('./SignAgreement/AgreementPdfViewer'));
const buildMaritalPayload = (requiresMaritalDisclosure, values) => {
    if (!requiresMaritalDisclosure || !values.married)
        return {};
    const married = values.married === 'Y';
    if (!married)
        return { married };
    const spouseLiveWith = values.spouseLiveWith === 'Y';
    return {
        married,
        spouseName: `${values.spouseFirstName ?? ''} ${values.spouseLastName ?? ''}`.trim(),
        spouseLiveWith,
        ...(spouseLiveWith
            ? {}
            : {
                spouseAddressLine1: values.spouseAddressLine1,
                spouseAddressLine2: values.spouseAddressLine2,
                spouseAddressCity: values.spouseAddressCity,
                spouseAddressState: values.spouseAddressState,
                spouseAddressZip: values.spouseAddressZip,
            }),
    };
};
const isMaritalDisclosureComplete = (requiresMaritalDisclosure, values) => {
    if (!requiresMaritalDisclosure)
        return true;
    if (!values.married)
        return false;
    if (values.married === 'N')
        return true;
    if (!values.spouseFirstName ||
        !values.spouseLastName ||
        !values.spouseLiveWith)
        return false;
    if (values.spouseLiveWith === 'Y')
        return true;
    return Boolean(values.spouseAddressLine1 &&
        values.spouseAddressCity &&
        values.spouseAddressState &&
        values.spouseAddressZip);
};
function SignAgreement() {
    const [ssnLast4, setSsnLast4] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [containerWidth, setContainerWidth] = useState(null);
    const [isAchAuthModalOpen, setIsAchAuthModalOpen] = useState(false);
    const observerRef = useRef(null);
    const containerRef = useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        if (!node)
            return;
        if (typeof ResizeObserver !== 'undefined') {
            observerRef.current = new ResizeObserver((entries) => {
                setContainerWidth(entries[0].contentRect.width);
            });
            observerRef.current.observe(node);
        }
        else {
            const measure = () => setContainerWidth(node.getBoundingClientRect().width);
            measure();
            window.addEventListener('resize', measure);
            // Store cleanup on the ref so it can be called when node unmounts
            observerRef.current = {
                disconnect: () => window.removeEventListener('resize', measure),
            };
        }
    }, []);
    const history = useHistory();
    const authorizationToken = useAuthorizationToken();
    const flowPath = useFlowPath(authorizationToken);
    const { instrumentId, finalizeData, isLoading } = useFlexFinalizeData(authorizationToken);
    const { data: instrument, isLoading: isInstrumentLoading, isFetching: isInstrumentFetching, } = useGetServicingV1InstrumentsInstrumentIdQuery({ instrumentId: instrumentId }, { skip: !instrumentId, refetchOnMountOrArgChange: true });
    // Derive autopayDayOfMonth from estimatedFirstPaymentDate if not provided by backend
    const autopayDayOfMonth = finalizeData?.autopayDayOfMonth ??
        (finalizeData?.estimatedFirstPaymentDate
            ? parseInt(finalizeData.estimatedFirstPaymentDate.split('-')[2], 10)
            : undefined);
    const isDI = !!finalizeData?.suggestedMonthlyPayment;
    // Use the payment option the customer actually selected, as returned by the
    // backend, rather than re-deriving it by comparing amounts (which broke on
    // decimal-precision mismatches). Fall back to 'minimum' to preserve the
    // single-row display when no autopay settings have been persisted yet.
    const paymentOption = finalizeData?.paymentOption ?? instrument?.paymentOption ?? 'minimum';
    const requiresEmail = !!finalizeData?.requiresEmail;
    const requiresMaritalDisclosure = !!finalizeData?.requiresMaritalDisclosure;
    const bankAccountId = useEffectiveBankAccountId(finalizeData);
    const handleSubmit = async (values) => {
        if (!isMaritalDisclosureComplete(requiresMaritalDisclosure, values))
            return;
        if (instrumentId && finalContractDoc?.id && arbitrationDoc?.id) {
            setSubmitting(true);
            setErrorMessage('');
            try {
                const achAuthDoc = await generateAchAuthorization({
                    instrumentId,
                    postServicingV1InstrumentsInstrumentIdAgreementsTermsAchAuthorization: {
                        ssnLastFour: ssnLast4,
                        bankAccountId: bankAccountId,
                    },
                }).unwrap();
                const achResult = await signAgreement({
                    instrumentId: instrumentId,
                    putServicingV1InstrumentsInstrumentIdAgreementSign: {
                        agreementId: Number(achAuthDoc.id),
                        ssnLastFour: ssnLast4,
                    },
                }).unwrap();
                const arbitrationResult = await signAgreement({
                    instrumentId: instrumentId,
                    putServicingV1InstrumentsInstrumentIdAgreementSign: {
                        agreementId: Number(arbitrationDoc.id),
                        ssnLastFour: ssnLast4,
                    },
                }).unwrap();
                const finalContractResult = await signAgreement({
                    instrumentId: instrumentId,
                    putServicingV1InstrumentsInstrumentIdAgreementSign: {
                        agreementId: Number(finalContractDoc.id),
                        ssnLastFour: ssnLast4,
                        ...(requiresEmail && values.email ? { email: values.email } : {}),
                        ...buildMaritalPayload(requiresMaritalDisclosure, values),
                    },
                }).unwrap();
                if (achResult && finalContractResult && arbitrationResult) {
                    history.push(flowPath('/sign-success'));
                }
            }
            catch (error) {
                setErrorMessage('An error occurred when signing, please contact support.');
                setSubmitting(false);
            }
        }
    };
    const handleGoBack = () => {
        history.push(flowPath('/autopay-auth'));
    };
    const handleDownloadPDF = () => {
        if (finalContractDoc?.attachment?.url) {
            window.open(finalContractDoc.attachment.url, '_blank');
        }
    };
    const handleLast4 = (e) => {
        if (/^[0-9]*$/.test(e.target.value) && e.target.value.length <= 4) {
            setSsnLast4(e.target.value);
        }
    };
    const { data: finalContractDoc } = useGetServicingV1InstrumentsInstrumentIdAgreementsTermsTemplateNameQuery({
        instrumentId: instrumentId,
        templateName: 'flex_contract',
    }, { skip: !instrumentId });
    const { data: arbitrationDoc } = useGetServicingV1InstrumentsInstrumentIdAgreementsTermsTemplateNameQuery({
        instrumentId: instrumentId,
        templateName: 'arbitration_provision',
    }, { skip: !instrumentId });
    const [generateAchAuthorization] = usePostServicingV1InstrumentsInstrumentIdAgreementsTermsAchAuthorizationMutation();
    const [signAgreement] = usePutServicingV1InstrumentsInstrumentIdAgreementSignMutation();
    if (isLoading ||
        isInstrumentLoading ||
        isInstrumentFetching ||
        !finalizeData) {
        return (_jsx("div", { className: "text-center", children: _jsx(Spinner, {}) }));
    }
    return (_jsx(Form, { onSubmit: handleSubmit, initialValues: {
            applicantName: `${finalizeData?.firstName} ${finalizeData?.lastName}`,
            email: '',
            agreedToTerms1: false,
            agreedToTerms2: false,
            agreedToTerms3: false,
        }, render: ({ handleSubmit, values, invalid }) => (_jsxs("form", { onSubmit: handleSubmit, children: [_jsxs("div", { className: "approve-charge", children: [_jsx("div", { className: "centered-header", children: _jsxs(H1, { variant: "headline-large", className: "text-center header-margin mar-b-20", children: ["One last step, ", finalizeData?.firstName] }) }), _jsxs("div", { className: "approve-charge mar-b-40", children: [_jsx(SummaryCard, { ...finalizeData, paymentOption: paymentOption, autopayEnabled: instrument?.autopayEnabled, autopayDayOfMonth: autopayDayOfMonth, showDownPaymentNote: true }), _jsx("div", { className: "agreement-container mar-t-40", children: _jsx("div", { ref: containerRef, className: "w100", children: finalContractDoc?.attachment?.url && containerWidth ? (_jsx(Suspense, { fallback: _jsx("div", { className: "flex-center", children: _jsx(Spinner, { size: "medium" }) }), children: _jsx(AgreementPdfViewer, { url: finalContractDoc.attachment.url, containerWidth: containerWidth }) })) : (_jsx("div", { className: "flex-center", children: _jsx(Spinner, { size: "medium" }) })) }) }), _jsx("div", { className: "flex justify-end mar-t-20", children: _jsx(ButtonV2, { type: "button", variant: "text", color: "blue", size: "medium", onClick: handleDownloadPDF, children: "Download PDF" }) }), _jsxs("div", { className: "esign-input-fields mar-t-40", children: [(requiresEmail || requiresMaritalDisclosure) && (_jsx("hr", { className: "flex-finalize-divider" })), requiresEmail && (_jsx(Field, { name: "email", component: InputText, label: "Email Address", placeholder: "Enter your email address", inputMode: "email", autoComplete: "email", fieldsetclass: "email-fieldset", validate: composeValidators(validateRequiredField, validateEmail) })), requiresEmail && requiresMaritalDisclosure && (_jsx("hr", { className: "flex-finalize-divider" })), requiresMaritalDisclosure && (_jsx(MaritalDisclosureFields, { married: values.married, spouseLiveWith: values.spouseLiveWith })), (requiresEmail || requiresMaritalDisclosure) && (_jsx("hr", { className: "flex-finalize-divider" })), _jsx("div", { className: "row", children: _jsxs("div", { className: "ssn-input-container ssn-input-container-full-width", children: [_jsx(Label, { htmlFor: "ssnLastFour", size: "medium", children: "Last 4 of SSN/ITIN #" }), _jsx(Field, { id: "ssnLastFour", component: InputNumber, name: "ssnLastFour", placeholder: "Enter last four of SSN/ITIN #", allowLeadingZeros: true, decimalScale: 0, isAllowed: (values) => {
                                                            const { value } = values;
                                                            return value.length <= 4;
                                                        }, onChange: handleLast4 })] }) }), _jsxs("div", { className: "checkbox-container flex mar-t-20", children: [_jsx(Field, { name: "agreedToTerms1", component: InputCheckbox, type: "checkbox", dataTestId: "agreedToTerms1", className: "mar-r-10" }), _jsx(Span, { variant: "body-large", className: "mar-t-16", children: "I agree to pay for these goods or services according to the terms above. If any purchase is not satisfactory, it will be subject to the provider's refund policy." })] }), _jsxs("div", { className: "checkbox-container flex mar-t-20", children: [_jsx(Field, { name: "agreedToTerms2", component: InputCheckbox, type: "checkbox", dataTestId: "agreedToTerms2", className: "mar-r-10" }), _jsxs(Span, { variant: "body-large", className: "mar-t-16", children: ["I agree to the", ' ', _jsx(A, { variant: "body-large", onClick: () => {
                                                                if (arbitrationDoc?.attachment?.url) {
                                                                    window.open(arbitrationDoc.attachment.url, '_blank');
                                                                }
                                                            }, className: "color-blue text-underline cursor-pointer", children: "Arbitration Provision" }), "."] })] }), _jsxs("div", { className: "checkbox-container flex mar-t-20", children: [_jsx(Field, { name: "agreedToTerms3", component: InputCheckbox, type: "checkbox", dataTestId: "agreedToTerms3", className: "mar-r-10" }), _jsxs(Span, { variant: "body-large", className: "mar-t-16", children: ["By selecting this checkbox and proceeding with \"I Agree\", you authorize Alphaeon Credit, Inc. to debit your account for the amount selected pursuant to the terms in the", ' ', _jsx(A, { variant: "body-large", onClick: () => setIsAchAuthModalOpen(true), className: "color-blue text-underline cursor-pointer", children: "Payment Authorization Consent Document" }), ", which includes information about your right to cancel."] })] })] }), errorMessage && (_jsx("div", { className: "flex-center mar-t-20", children: _jsx(Span, { variant: "body-large", className: "red-base", children: errorMessage }) })), _jsx(FlowActions, { className: "mar-b-20", secondary: _jsx(ButtonV2, { type: "button", variant: "ghost", color: "orange-flex", size: "medium", hugText: true, onClick: handleGoBack, children: "Go Back" }), children: _jsx(ButtonV2, { type: "submit", variant: "filled", color: "orange-flex", size: "medium", hugText: true, submitting: submitting, disabled: ssnLast4.length < 4 ||
                                            (requiresEmail && invalid) ||
                                            !isMaritalDisclosureComplete(requiresMaritalDisclosure, values) ||
                                            !values.agreedToTerms1 ||
                                            !values.agreedToTerms2 ||
                                            !values.agreedToTerms3, children: "I Agree" }) }), _jsx(DisclosureFooter, { termLength: finalizeData?.product?.termLength, loanAmount: finalizeData?.loanAmount, calculatedApr: finalizeData?.calculatedApr, promotionalPeriod: isDI ? finalizeData?.product?.promotionalPeriod : undefined })] })] }), _jsx(AchAuthSsnModal, { isOpen: isAchAuthModalOpen, setIsOpen: setIsAchAuthModalOpen, instrumentId: instrumentId, bankAccountId: bankAccountId })] })) }));
}
export default SignAgreement;
